import type { PublicProfile } from "@pax/api-contract/common";
import type { ModerationInputKind } from "@pax/api-contract/moderation/v1alpha1";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type SubmitEvent, useState } from "react";
import { ProfileAppeals } from "@/features/moderation/ItemAppeals";
import { SubmitError } from "@/features/moderation/SubmitError";
import { api, reachable } from "@/lib/api";
import { profileQuery } from "../queries";

/** The profile's text fields that save through Update profile, one dialog each. */
export type ProfileField = "displayName" | "bio" | "region";

const FIELDS: Record<
  ProfileField,
  { title: string; maxLength: number; required: boolean; inputKind: ModerationInputKind }
> = {
  displayName: {
    title: "Display name",
    maxLength: 50,
    required: true,
    inputKind: "profile_display_name",
  },
  bio: { title: "Bio", maxLength: 500, required: false, inputKind: "profile_bio" },
  region: { title: "Region", maxLength: 100, required: false, inputKind: "profile_region" },
};

/** One of your display name, bio, or region, on its own. */
export function ProfileFieldDialog({
  profile,
  field,
  onOpenChange,
}: {
  profile: PublicProfile;
  /** The field being edited, or null while the dialog is closed. */
  field: ProfileField | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [shown, setShown] = useState(field);
  if (field !== null && field !== shown) setShown(field);

  return (
    <Dialog open={field !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {shown ? (
          <>
            <DialogHeader>
              <DialogTitle>{FIELDS[shown].title}</DialogTitle>
            </DialogHeader>
            {/* Mounted with the dialog, so every opening starts from what the profile says now. */}
            {field ? (
              <FieldForm
                key={field}
                profile={profile}
                field={field}
                onDone={() => onOpenChange(false)}
              />
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FieldForm({
  profile,
  field,
  onDone,
}: {
  profile: PublicProfile;
  field: ProfileField;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const { title, maxLength, required, inputKind } = FIELDS[field];
  const [value, setValue] = useState(profile[field] ?? "");
  const trimmed = value.trim();

  const save = useMutation({
    mutationFn: () =>
      reachable(
        api.profiles.update({
          params: { profileId: profile.profileId },
          body: field === "displayName" ? { displayName: trimmed } : { [field]: trimmed || null },
        }),
      ),
    onSuccess: response => {
      queryClient.setQueryData(profileQuery(profile.profileId).queryKey, response);
      onDone();
    },
  });

  function submit(event: SubmitEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        {field === "bio" ? (
          <Textarea
            aria-label={title}
            value={value}
            onChange={event => setValue(event.target.value)}
            maxLength={maxLength}
            rows={4}
            autoFocus
          />
        ) : (
          <Input
            aria-label={title}
            value={value}
            onChange={event => setValue(event.target.value)}
            maxLength={maxLength}
            required={required}
            placeholder={field === "region" ? "Where you are, if you'd like to say" : undefined}
            autoFocus
          />
        )}
        <p className="text-right text-xs text-muted-foreground tabular-nums">
          {value.length}/{maxLength}
        </p>
      </div>

      <ProfileAppeals profileId={profile.profileId} inputKinds={[inputKind]} />
      <SubmitError error={save.error} inputs={title.toLowerCase()} />

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending || (required && !trimmed)}>
          Save
        </Button>
      </DialogFooter>
    </form>
  );
}
